import {ChangeDetectorRef, Component} from '@angular/core';
import {MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTabsModule} from '@angular/material/tabs';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Router} from '@angular/router';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {MapService} from '../api/services/map.service';
import {JobService} from '../api/services/job.service';
import {NotificationService} from '../services/notification.service';
import {GenerationStateService} from '../services/generation-state.service';
import {Job} from '../api/interfaces/jobs.dto';
import {smapTaskKey} from '@maporium-workspace/shared';

/**
 * One import source = one tab. Adding a new source (CSV, …) means appending an
 * entry here and a branch in `startImport`; the template is driven by this list.
 */
export interface ImportSmapTab {
  id: string;
  labelKey: string;
  icon: string;
  accept: string;
  instructionsKey: string;
}

@Component({
  selector: 'maporium-import-smap-dialog',
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <mat-icon class="material-symbols-rounded me-2">publish</mat-icon>
        {{ 'IMPORT_SMAP_DIALOG.TITLE' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" mat-dialog-close [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>

    <div mat-dialog-content class="tabbed-content">
      <mat-tab-group
        [selectedIndex]="activeTabIndex"
        (selectedIndexChange)="activeTabIndex = $event"
        class="import-smap-tabs">
        @for (tab of tabs; track tab.id) {
          <mat-tab>
            <ng-template mat-tab-label>
              <span [ngClass]="tab.icon"></span>{{ tab.labelKey | translate }}
            </ng-template>
            <div class="tab-body file-tab-body">
              <div class="file-upload-area">
                <span [ngClass]="tab.icon" class="file-icon"></span>
                <p class="file-instructions" [class.chosen-file-name]="!!selectedFileNames[tab.id]">
                  {{ selectedFileNames[tab.id] || (tab.instructionsKey | translate) }}
                </p>
                <input #fileInput type="file" name="file" [accept]="tab.accept" hidden
                       (change)="onFileSelected(tab.id, fileInput)">
                <button type="button" class="choose-file-btn" (click)="fileInput.click()">
                  {{ (selectedFileNames[tab.id] ? 'CREATE_MAP_DIALOG.CHANGE_FILE' : 'CREATE_MAP_DIALOG.CHOOSE_FILE') | translate }}
                </button>
              </div>
            </div>
          </mat-tab>
        }
      </mat-tab-group>
    </div>

    <div mat-dialog-actions class="d-flex align-items-center justify-content-evenly">
      <button class="dialog-action-btn dialog-action-btn-cancel" mat-dialog-close [disabled]="isImporting">
        {{ 'CREATE_MAP_DIALOG.CANCEL' | translate }}
      </button>
      <button class="dialog-action-btn dialog-action-btn-primary" (click)="startImport()"
              [disabled]="isImporting || !hasFileForActiveTab()">
        {{ isImporting ? ('CREATE_MAP_DIALOG.UPLOADING' | translate) : ('IMPORT_MAP.IMPORT' | translate) }}
      </button>
    </div>
  `,
  styles: [`
    .tabbed-content {
      padding: 4px 0 0 0 !important;
      overflow: visible;
    }

    .tab-body {
      padding: 0;
      min-height: 160px;
    }

    .file-tab-body {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .file-upload-area {
      text-align: center;
      padding: 24px 16px;
    }

    .file-icon {
      font-size: 48px;
      color: #aaa;
      display: block;
      margin-bottom: 8px;
    }

    .file-instructions {
      color: #666;
      font-size: 13px;
      margin: 0 0 16px 0;
      word-break: break-word;
    }

    .file-instructions.chosen-file-name {
      color: #3D85C6;
      font-weight: 500;
    }

    .choose-file-btn {
      font-family: 'Oswald', sans-serif;
      font-size: 15px;
      letter-spacing: 0.5px;
      padding: 0 24px;
      border: 1.5px solid #3D85C6;
      border-radius: 6px;
      background-color: white;
      color: #3D85C6;
      cursor: pointer;
      text-transform: uppercase;
      height: 29px;
      box-sizing: border-box;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .choose-file-btn:hover {
      background-color: #f0f5fa;
    }

    ::ng-deep .import-smap-tabs {
      --mat-tab-header-label-text-size: 17px;
      --mat-tab-header-inactive-label-text-color: #5E707F;
      --mat-tab-header-inactive-focus-label-text-color: #5E707F;
      --mat-tab-header-inactive-hover-label-text-color: #5E707F;
      --mat-tab-header-active-label-text-color: #3D85C6;
      --mat-tab-header-active-focus-label-text-color: #3D85C6;
      --mat-tab-header-active-hover-label-text-color: #3D85C6;
      --mat-tab-header-active-ripple-color: #3D85C6;
      --mat-tab-header-inactive-ripple-color: #3D85C6;
      --mdc-tab-indicator-active-indicator-color: #3D85C6;

      .mat-mdc-tab-header {
        margin-bottom: 0;
        border-bottom: 1px solid #e0e0e0;
      }

      .mat-mdc-tab-labels {
        justify-content: center;
      }

      .mat-mdc-tab {
        min-width: 0;
        padding: 0 20px;
        font-family: 'Oswald', sans-serif;
        font-size: 17px;
        letter-spacing: 0.5px;
        height: 48px;
        flex-grow: 0;
      }

      .mdc-tab__text-label {
        display: flex;
        align-items: center;
        gap: 0;
        font-size: 17px;
        line-height: 1;
      }

      .mdc-tab__text-label [class*='icon-smap-'] {
        font-size: 22px;
        line-height: 1;
      }

      .mat-mdc-tab-body-wrapper {
        background: #f6faff;
        border-radius: 0 0 8px 8px;
        min-height: 180px;
      }

      .mat-mdc-tab-body-content {
        padding: 8px 16px;
      }

      .mat-mdc-tab-header-pagination {
        display: none !important;
      }
    }
  `],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatTooltipModule,
    TranslatePipe
  ],
  standalone: true
})
export class ImportSmapDialogComponent {
  readonly tabs: ImportSmapTab[] = [
    {
      id: 'smap-archive',
      labelKey: 'IMPORT_SMAP_DIALOG.TAB_SMAP_ARCHIVE',
      icon: 'icon-smap-format-smap',
      accept: '.smap',
      instructionsKey: 'IMPORT_SMAP_DIALOG.SMAP_ARCHIVE_INSTRUCTIONS'
    }
  ];

  activeTabIndex = 0;
  isImporting = false;
  selectedFileNames: Record<string, string> = {};
  private selectedFiles: Record<string, File | null> = {};

  constructor(
    private mapService: MapService,
    private jobService: JobService,
    private router: Router,
    private notificationService: NotificationService,
    private generationStateService: GenerationStateService,
    private matDialogRef: MatDialogRef<ImportSmapDialogComponent>,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {
  }

  onFileSelected(tabId: string, input: HTMLInputElement): void {
    const file = input.files?.length ? input.files[0] : null;
    this.selectedFiles[tabId] = file;
    this.selectedFileNames[tabId] = file?.name || '';
    this.cdr.detectChanges();
  }

  hasFileForActiveTab(): boolean {
    return !!this.selectedFiles[this.tabs[this.activeTabIndex]?.id];
  }

  startImport(): void {
    if (this.isImporting) return;
    const tab = this.tabs[this.activeTabIndex];
    const file = tab && this.selectedFiles[tab.id];
    if (!file) {
      this.notificationService.warning(this.translate.instant('IMPORT_MAP.SELECT_FILE_WARNING'));
      return;
    }

    this.isImporting = true;
    switch (tab.id) {
      case 'smap-archive':
        this.importSmapArchive(file);
        break;
      default:
        this.isImporting = false;
    }
  }

  private importSmapArchive(file: File): void {
    const progressNotification = this.notificationService.loading(this.translate.instant(smapTaskKey('smapImport', 'UPLOADING')));

    this.mapService.importMapFromZip(file).subscribe({
      next: (job: Job) => {
        // The upload card becomes the "in progress" card: same notification id,
        // so it morphs in place. Dismissing it and raising a second, look-alike
        // one made the upload response read as a flash.
        this.notificationService.update(progressNotification.id, {
          type: 'info',
          message: this.translate.instant(smapTaskKey('smapImport', 'IN_PROGRESS_INFO')),
          duration: 8000,
          dismissible: true,
          taskKey: job.id
        });
        this.trackImportJob(job);
        this.matDialogRef.close();
      },
      error: (error: unknown) => {
        this.isImporting = false;
        progressNotification.dismiss();
        this.notificationService.error(this.translate.instant(smapTaskKey('smapImport', 'START_FAILED')), {
          title: this.translate.instant(smapTaskKey('smapImport', 'FAILED_TITLE')),
          duration: 0
        });
        console.error('Smap import error:', error);
      }
    });
  }

  private trackImportJob(job: Job): void {
    this.jobService.trackJob(job.id, (updatedJob: Job) => {
      if (updatedJob.status === 'COMPLETED' || updatedJob.status === 'FAILED') {
        this.generationStateService.endSmapGeneration(updatedJob.id);
      }
      if (updatedJob.status === 'COMPLETED') {
        this.notificationService.success(
          this.translate.instant(smapTaskKey('smapImport', 'SUCCESS_MESSAGE')),
          {
            title: this.translate.instant(smapTaskKey('smapImport', 'SUCCESS_TITLE')),
            taskKey: updatedJob.id,
            action: {
              label: this.translate.instant('CREATE_MAP_DIALOG.OPEN_SMAP'),
              handler: (onClose) => {
                if (updatedJob.result?.entityId) {
                  const entityId = updatedJob.result.entityId;
                  onClose();
                  void this.router.navigate(['/dashboard/map', entityId]);
                }
              }
            }
          }
        );
      } else if (updatedJob.status === 'FAILED') {
        this.notificationService.error(
          updatedJob.error || this.translate.instant(smapTaskKey('smapImport', 'FAILED_DEFAULT')),
          {
            title: this.translate.instant(smapTaskKey('smapImport', 'FAILED_TITLE')),
            duration: 0,
            taskKey: updatedJob.id
          }
        );
      }
    });
  }
}
